import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ShoppingCart,
  TrendingUp,
  Settings,
  ArrowRightLeft,
  ClipboardCheck,
  LayoutGrid,
  TableIcon,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Package,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { StockTransaction, TransactionType } from "@/types/stock-transaction";
import { getTransactionsByStockCode } from "@/data/mockTransactions";

interface StockItemTransactionHistoryTabProps {
  stockItemCode: string;
  onNavigateToTransaction?: (type: TransactionType, reference: string, transactionId: string) => void;
}

type ViewMode = "card" | "table";

// Transaction type configuration
const TRANSACTION_CONFIGS: Record<
  TransactionType,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bgColor: string;
    borderColor: string;
  }
> = {
  "purchase-order": {
    label: "Purchase Order",
    icon: ShoppingCart,
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    borderColor: "border-blue-300",
  },
  "sales-order": {
    label: "Sales Order",
    icon: TrendingUp,
    color: "text-green-700",
    bgColor: "bg-green-100",
    borderColor: "border-green-300",
  },
  "stock-adjustment": {
    label: "Stock Adjustment",
    icon: Settings,
    color: "text-yellow-700",
    bgColor: "bg-yellow-100",
    borderColor: "border-yellow-300",
  },
  "stock-transfer": {
    label: "Stock Transfer",
    icon: ArrowRightLeft,
    color: "text-purple-700",
    bgColor: "bg-purple-100",
    borderColor: "border-purple-300",
  },
  "stock-take": {
    label: "Stock Take",
    icon: ClipboardCheck,
    color: "text-indigo-700",
    bgColor: "bg-indigo-100",
    borderColor: "border-indigo-300",
  },
};

// Format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
};

// Format date
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// Format relative time
const formatRelativeTime = (date: Date): string => {
  return formatDistanceToNow(date, { addSuffix: true });
};

export function StockItemTransactionHistoryTab({
  stockItemCode,
  onNavigateToTransaction,
}: StockItemTransactionHistoryTabProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("card");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Get transactions for this stock item
  const transactions = useMemo(() => {
    return getTransactionsByStockCode(stockItemCode);
  }, [stockItemCode]);

  // Calculate pagination
  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = transactions.slice(startIndex, endIndex);

  // Handle reference click
  const handleReferenceClick = (transaction: StockTransaction) => {
    if (onNavigateToTransaction) {
      onNavigateToTransaction(transaction.type, transaction.reference, transaction.id);
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Empty state
  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Package className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No transactions found</h3>
        <p className="text-sm text-muted-foreground">
          No transactions found for this stock item
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with view toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Transaction History</h3>
          <p className="text-sm text-muted-foreground">
            {transactions.length} transaction{transactions.length !== 1 ? "s" : ""} found
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "card" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("card")}
          >
            <LayoutGrid className="h-4 w-4 mr-2" />
            Card View
          </Button>
          <Button
            variant={viewMode === "table" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("table")}
          >
            <TableIcon className="h-4 w-4 mr-2" />
            Table View
          </Button>
        </div>
      </div>

      {/* Card View */}
      {viewMode === "card" && (
        <div className="space-y-3">
          {paginatedTransactions.map((transaction) => {
            const config = TRANSACTION_CONFIGS[transaction.type];
            const Icon = config.icon;
            const isIncrease = transaction.quantity > 0;

            return (
              <Card
                key={transaction.id}
                className={`border-l-4 ${config.borderColor} hover:shadow-md transition-shadow`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.bgColor}`}>
                        <Icon className={`h-5 w-5 ${config.color}`} />
                      </div>
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Badge className={`${config.bgColor} ${config.color} border-0`}>
                            {config.label}
                          </Badge>
                          <Button
                            variant="link"
                            className="p-0 h-auto font-mono text-sm"
                            onClick={() => handleReferenceClick(transaction)}
                          >
                            {transaction.reference}
                          </Button>
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(transaction.date)}
                          <span className="text-xs">({formatRelativeTime(transaction.date)})</span>
                        </CardDescription>
                      </div>
                    </div>
                    <div className="text-right">
                      <div
                        className={`text-lg font-bold ${
                          isIncrease ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        {isIncrease ? "+" : ""}
                        {transaction.quantity}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Balance: {transaction.runningBalance}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {transaction.party && (
                      <div>
                        <span className="text-muted-foreground">Party/Location:</span>
                        <div className="font-medium">{transaction.party}</div>
                      </div>
                    )}
                    {transaction.costPrice && (
                      <div>
                        <span className="text-muted-foreground">Cost Price:</span>
                        <div className="font-medium">{formatCurrency(transaction.costPrice)}</div>
                      </div>
                    )}
                    {transaction.sellingPrice && (
                      <div>
                        <span className="text-muted-foreground">Selling Price:</span>
                        <div className="font-medium">{formatCurrency(transaction.sellingPrice)}</div>
                      </div>
                    )}
                  </div>
                  {transaction.notes && (
                    <div className="mt-3 p-2 bg-muted/50 rounded text-sm">
                      <span className="text-muted-foreground">Notes: </span>
                      {transaction.notes}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Party/Location</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead className="text-right">Running Balance</TableHead>
                <TableHead className="text-right">Cost Price</TableHead>
                <TableHead className="text-right">Selling Price</TableHead>
                <TableHead>Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTransactions.map((transaction) => {
                const config = TRANSACTION_CONFIGS[transaction.type];
                const Icon = config.icon;
                const isIncrease = transaction.quantity > 0;

                return (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      <div className="text-sm">
                        <div>{formatDate(transaction.date)}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatRelativeTime(transaction.date)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${config.bgColor} ${config.color} border-0 gap-1`}>
                        <Icon className="h-3 w-3" />
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto font-mono text-sm"
                        onClick={() => handleReferenceClick(transaction)}
                      >
                        {transaction.reference}
                      </Button>
                    </TableCell>
                    <TableCell>{transaction.party || "-"}</TableCell>
                    <TableCell
                      className={`text-right font-semibold ${
                        isIncrease ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {isIncrease ? "+" : ""}
                      {transaction.quantity}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {transaction.runningBalance}
                    </TableCell>
                    <TableCell className="text-right">
                      {transaction.costPrice ? formatCurrency(transaction.costPrice) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      {transaction.sellingPrice ? formatCurrency(transaction.sellingPrice) : "-"}
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                      {transaction.notes || "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination - only show if more than 20 transactions */}
      {transactions.length > itemsPerPage && (
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, transactions.length)} of{" "}
            {transactions.length} transactions
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="flex gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className="w-8 h-8 p-0"
                >
                  {page}
                </Button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
